import React, { useState, useEffect } from 'react';
import { LogOut, Upload, GripVertical, BarChart3, TrendingUp, Users, Target, Award, UserPlus, Send, X } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Checkbox } from "./ui/checkbox";
import * as XLSX from 'xlsx';

const CRMDashboard = ({ onLogout }) => {
  const [activeCategory, setActiveCategory] = useState('recrutamento');
  const [leadsData, setLeadsData] = useState({
    recrutamento: [],
    compradores: [],
    vendedores: [],
    arrendamento: [],
    investidores: []
  });
  const [loading, setLoading] = useState(false);

  const [selectedLead, setSelectedLead] = useState(null);
  const [draggedLead, setDraggedLead] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [showDistributeByQuantityModal, setShowDistributeByQuantityModal] = useState(false);
  const [distributeQuantity, setDistributeQuantity] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '' });
  
  const leads = leadsData[activeCategory] || [];
  
  // Carregar leads do backend
  useEffect(() => {
    loadLeads();
    loadUsers();
  }, [activeCategory]);
  
  const loadLeads = async () => {
    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/leads/?category=${activeCategory}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch leads');
      
      const data = await response.json();
      
      // Mapear dados do backend para formato do frontend
      const mappedLeads = data.map(lead => ({
        id: lead.id,
        name: lead.name,
        phone: lead.phone,
        email: lead.email || '',
        company: lead.company || '',
        position: lead.position || '',
        agency: lead.agency || '',
        status: lead.status,
        temperature: lead.temperature,
        notes: lead.notes,
        assignedTo: lead.assigned_to,
        lastContact: lead.last_contact,
        source: lead.source,
        isNew: lead.is_new
      }));
      
      setLeadsData(prev => ({
        ...prev,
        [activeCategory]: mappedLeads
      }));
    } catch (error) {
      console.error('Error loading leads:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadUsers = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/users/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch users');
      
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };
  
  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };
  
  const selectAllLeads = () => {
    if (selectedLeads.length === leads.length && leads.length > 0) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(l => l.id));
    }
  };

  const columns = [
    { id: "não contactado", title: "Não Contactado", color: "bg-gray-200" },
    { id: "1º contacto", title: "1º Contacto", color: "bg-blue-200" },
    { id: "2º contacto", title: "2º Contacto", color: "bg-blue-300" },
    { id: "3º contacto", title: "3º Contacto", color: "bg-blue-400" },
    { id: "reunião", title: "Reunião", color: "bg-purple-200" },
    { id: "lead perdida", title: "Lead Perdida", color: "bg-red-200" },
    { id: "lead ganha", title: "Lead Ganha", color: "bg-green-200" }
  ];

  const temperatureColors = { frio: "bg-blue-500", morno: "bg-yellow-500", quente: "bg-red-500" };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (draggedLead) {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('authToken');
        
        await fetch(`${API_URL}/api/leads/${draggedLead.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: newStatus,
            is_new: false
          }),
        });
        
        // Atualizar localmente
        const updatedLeads = leads.map(lead => 
          lead.id === draggedLead.id 
            ? { ...lead, status: newStatus, isNew: false } 
            : lead
        );
        setLeadsData({ ...leadsData, [activeCategory]: updatedLeads });
        setDraggedLead(null);
      } catch (error) {
        console.error('Error updating lead status:', error);
      }
    }
  };

  const updateLead = async (updatedLead) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/leads/${updatedLead.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: updatedLead.name,
          phone: updatedLead.phone,
          email: updatedLead.email,
          company: updatedLead.company,
          position: updatedLead.position,
          agency: updatedLead.agency,
          status: updatedLead.status,
          temperature: updatedLead.temperature,
          notes: updatedLead.notes,
          assigned_to: updatedLead.assignedTo,
          is_new: false
        }),
      });
      
      if (!response.ok) throw new Error('Failed to update lead');
      
      // Recarregar leads
      await loadLeads();
      setSelectedLead(null);
    } catch (error) {
      console.error('Error updating lead:', error);
      alert('Erro ao atualizar lead');
    }
  };

  const parseExcel = (data) => {
    try {
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(firstSheet);
      
      const newLeads = jsonData.map((row, index) => ({
        id: Date.now() + index,
        name: row['Nome'] || row['NOME'] || row['nome'] || '',
        phone: row['Telefone'] || row['TELEFONE'] || row['telefone'] || row['Telemóvel'] || row['Contacto'] || '',
        email: row['Email'] || row['EMAIL'] || row['email'] || row['E-mail'] || '',
        company: row['Empresa'] || row['EMPRESA'] || row['empresa'] || '',
        position: row['Position'] || row['POSITION'] || row['position'] || row['Cargo'] || '',
        agency: row['Agência'] || row['AGÊNCIA'] || row['agência'] || row['Agency'] || row['AGENCY'] || '',
        status: 'não contactado',
        temperature: 'frio',
        notes: '',
        assignedTo: 'Não atribuído',
        lastContact: new Date().toISOString().split('T')[0],
        source: 'import',
        isNew: false
      })).filter(lead => lead.name);
      
      return newLeads;
    } catch (error) {
      console.error('Error parsing Excel:', error);
      return [];
    }
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(/[,;]/).map(h => h.trim().toLowerCase());
    
    const newLeads = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(/[,;]/).map(v => v.trim());
      
      if (values.length >= 2) {
        const lead = {
          id: Date.now() + i,
          name: values[headers.indexOf('nome') !== -1 ? headers.indexOf('nome') : 0] || '',
          phone: values[headers.indexOf('telefone') !== -1 ? headers.indexOf('telefone') : 1] || '',
          email: values[headers.indexOf('email') !== -1 ? headers.indexOf('email') : 2] || '',
          company: values[headers.indexOf('empresa') !== -1 ? headers.indexOf('empresa') : 3] || '',
          status: 'não contactado',
          temperature: 'frio',
          notes: '',
          assignedTo: 'Não atribuído',
          lastContact: new Date().toISOString().split('T')[0],
          source: 'import',
          isNew: false
        };
        
        if (lead.name) {
          newLeads.push(lead);
        }
      }
    }
    return newLeads;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('authToken');
      
      console.log('📤 Uploading file:', file.name, 'to category:', activeCategory);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/leads/import/${activeCategory}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to import file');
      }
      
      const result = await response.json();
      
      console.log('✅ Upload successful:', result);
      
      // Recarregar leads
      await loadLeads();
      
      const categoryName = categories.find(c => c.id === activeCategory)?.name || activeCategory;
      
      // Show success message
      const message = `✅ ${result.count} leads importadas com sucesso para ${categoryName}!`;
      alert(message);
      console.log(message);
      
    } catch (error) {
      console.error('❌ Error uploading file:', error);
      const errorMessage = '❌ Erro ao importar ficheiro: ' + error.message;
      alert(errorMessage);
      console.error(errorMessage);
    } finally {
      setLoading(false);
      e.target.value = ''; // Reset file input
    }
  };
  
  const distributeLeads = async (userId) => {
    const selectedUser = users.find(u => u.id === userId);
    if (!selectedUser) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/leads/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lead_ids: selectedLeads,
          user_id: userId
        }),
      });
      
      if (!response.ok) throw new Error('Failed to assign leads');
      
      // Recarregar leads
      await loadLeads();
      setSelectedLeads([]);
      setShowDistributeModal(false);
      alert(`✅ ${selectedLeads.length} leads atribuídas a ${selectedUser.name}!`);
    } catch (error) {
      console.error('Error distributing leads:', error);
      alert('Erro ao distribuir leads');
    }
  };
  
  const distributeLeadsByQuantity = async (userId) => {
    const selectedUser = users.find(u => u.id === userId);
    if (!selectedUser) return;
    
    const quantity = parseInt(distributeQuantity);
    if (!quantity || quantity <= 0) {
      alert('Por favor insira um número válido');
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/leads/assign-by-quantity?category=${activeCategory}&user_id=${userId}&quantity=${quantity}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to assign leads');
      }
      
      const result = await response.json();
      
      // Recarregar leads
      await loadLeads();
      setDistributeQuantity('');
      setShowDistributeByQuantityModal(false);
      alert(`✅ ${result.assigned} leads não atribuídas foram distribuídas a ${selectedUser.name}!`);
    } catch (error) {
      console.error('Error distributing leads by quantity:', error);
      alert('Erro ao distribuir leads: ' + error.message);
    }
  };
  
  const addUser = async () => {
    if (!newUser.name || !newUser.email) {
      alert('Por favor preencha nome e email');
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newUser.name,
          email: newUser.email,
          password: newUser.password || 'changeme123',
          role: 'user'
        }),
      });
      
      if (!response.ok) throw new Error('Failed to create user');
      
      const newUserData = await response.json();
      setUsers([...users, newUserData]);
      setNewUser({ name: '', email: '', password: '' });
      setShowUserModal(false);
      alert(`✅ Utilizador ${newUserData.name} criado com sucesso!`);
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Erro ao criar utilizador: ' + error.message);
    }
  };

  // Estatísticas
  const getUserStats = () => {
    const allLeads = Object.values(leadsData).flat();
    const users = [...new Set(allLeads.map(l => l.assignedTo))];
    return users.map(user => {
      const userLeads = allLeads.filter(l => l.assignedTo === user);
      const leadsGanhas = userLeads.filter(l => l.status === 'lead ganha').length;
      const leadsPerdidas = userLeads.filter(l => l.status === 'lead perdida').length;
      const emAndamento = userLeads.filter(l => !['lead ganha', 'lead perdida'].includes(l.status)).length;
      const taxaConversao = userLeads.length > 0 ? ((leadsGanhas / userLeads.length) * 100).toFixed(1) : 0;
      
      return {
        user,
        total: userLeads.length,
        leadsGanhas,
        leadsPerdidas,
        emAndamento,
        taxaConversao
      };
    });
  };

  const getGeneralStats = () => {
    const total = leads.length;
    const ganhas = leads.filter(l => l.status === 'lead ganha').length;
    const perdidas = leads.filter(l => l.status === 'lead perdida').length;
    const emAndamento = leads.filter(l => !['lead ganha', 'lead perdida'].includes(l.status)).length;
    const taxaConversao = total > 0 ? ((ganhas / total) * 100).toFixed(1) : 0;
    const doWebsite = leads.filter(l => l.source === 'website').length;
    
    return { total, ganhas, perdidas, emAndamento, taxaConversao, doWebsite };
  };

  const userStats = getUserStats();
  const generalStats = getGeneralStats();
  
  const categories = [
    { id: 'compradores', name: 'Clientes Compradores', icon: '🏠' },
    { id: 'vendedores', name: 'Clientes Vendedores', icon: '💰' },
    { id: 'arrendamento', name: 'Clientes de Arrendamento', icon: '🔑' },
    { id: 'investidores', name: 'Clientes Investidores', icon: '📈' },
    { id: 'recrutamento', name: 'Recrutamento', icon: '👔' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-r from-gold to-gold-light shadow-md sticky top-0 z-50">
        <div className="max-w-full mx-auto px-6 py-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold text-black heading-luxury">Área de Negócio - CRM</h1>
            <div className="flex items-center gap-2">
              {selectedLeads.length > 0 && (
                <Button 
                  onClick={() => setShowDistributeModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Distribuir ({selectedLeads.length})
                </Button>
              )}
              
              <Button 
                variant="outline" 
                className="bg-blue-600 text-white border-2 border-blue-600 hover:bg-blue-700"
                onClick={() => setShowDistributeByQuantityModal(true)}
              >
                <Send className="h-4 w-4 mr-2" />
                Distribuir por Nº
              </Button>
              
              <Button 
                variant="outline" 
                className="bg-white text-black border-2 border-black hover:bg-gray-100"
                onClick={() => setShowUserModal(true)}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Utilizadores
              </Button>
              
              <label 
                htmlFor="csv-upload" 
                className="inline-flex items-center justify-center px-4 py-2 border-2 border-black bg-white text-black hover:bg-gray-100 rounded-md cursor-pointer transition-colors font-medium"
                title={`Importar para ${categories.find(c => c.id === activeCategory)?.name}`}
              >
                <Upload className="h-4 w-4 mr-2" />
                Importar {categories.find(c => c.id === activeCategory)?.icon}
              </label>
              <input id="csv-upload" type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={handleFileUpload} />
              
              <Button variant="outline" className="bg-white text-black border-2 border-black hover:bg-gray-100" onClick={onLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
          
          {/* Categories Navigation */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map(cat => (
              <Button
                key={cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setSelectedLeads([]);
                }}
                className={`whitespace-nowrap ${
                  activeCategory === cat.id
                    ? 'bg-black text-white hover:bg-gray-800'
                    : 'bg-white text-black hover:bg-gray-100 border-2 border-black'
                }`}
              >
                <span className="mr-2">{cat.icon}</span>
                {cat.name}
                {leadsData[cat.id]?.length > 0 && (
                  <Badge className="ml-2 bg-white text-black">
                    {leadsData[cat.id].length}
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <Tabs defaultValue="kanban" className="p-6">
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
          <TabsTrigger value="kanban" className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            Kanban Board
          </TabsTrigger>
          <TabsTrigger value="stats" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Estatísticas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stats" className="space-y-6">
          {/* Info sobre importação */}
          <Card className="border-2 border-blue-300 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="text-2xl">ℹ️</div>
                <div className="flex-1">
                  <h3 className="font-bold text-blue-900 mb-2">Como importar leads via CSV</h3>
                  <p className="text-sm text-blue-800 mb-2">
                    Seleccione a categoria desejada e clique em "Importar para [categoria]". 
                    As leads serão adicionadas apenas à categoria activa.
                  </p>
                  <p className="text-xs text-blue-700 font-mono bg-white p-2 rounded">
                    Formato CSV: Nome, Telefone, Email, Empresa<br/>
                    Exemplo: João Silva, +351 912 345 678, joao@email.pt, Empresa ABC
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Estatísticas Gerais */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card className="border-2 border-gray-300">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Total de Leads
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-black">{generalStats.total}</div>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-300 bg-purple-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-purple-700 flex items-center gap-2">
                  🌐 Do Website
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-700">{generalStats.doWebsite}</div>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-300 bg-green-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Leads Ganhas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-700">{generalStats.ganhas}</div>
              </CardContent>
            </Card>

            <Card className="border-2 border-red-300 bg-red-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-red-700">Leads Perdidas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-700">{generalStats.perdidas}</div>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-300 bg-blue-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-blue-700 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Taxa de Conversão
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-700">{generalStats.taxaConversao}%</div>
              </CardContent>
            </Card>
          </div>

          {/* Estatísticas por Utilizador */}
          <Card className="border-2 border-gray-300">
            <CardHeader>
              <CardTitle className="text-2xl font-bold heading-luxury">Desempenho por Utilizador</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {userStats.map((stat, index) => (
                  <Card key={index} className="border-2 bg-gradient-to-r from-gray-50 to-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-gold to-gold-light rounded-full flex items-center justify-center">
                            <span className="text-xl font-bold text-black">{stat.user.charAt(0)}</span>
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-black">{stat.user}</h3>
                            <p className="text-sm text-gray-600">Consultor Imobiliário</p>
                          </div>
                        </div>
                        <Badge className="bg-gradient-to-r from-gold to-gold-light text-black text-lg px-4 py-2">
                          {stat.taxaConversao}% conversão
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-white rounded-lg border-2">
                          <div className="text-2xl font-bold text-black">{stat.total}</div>
                          <div className="text-xs text-gray-600 mt-1">Total</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg border-2 border-green-300">
                          <div className="text-2xl font-bold text-green-700">{stat.leadsGanhas}</div>
                          <div className="text-xs text-green-700 mt-1">Ganhas</div>
                        </div>
                        <div className="text-center p-3 bg-red-50 rounded-lg border-2 border-red-300">
                          <div className="text-2xl font-bold text-red-700">{stat.leadsPerdidas}</div>
                          <div className="text-xs text-red-700 mt-1">Perdidas</div>
                        </div>
                        <div className="text-center p-3 bg-blue-50 rounded-lg border-2 border-blue-300">
                          <div className="text-2xl font-bold text-blue-700">{stat.emAndamento}</div>
                          <div className="text-xs text-blue-700 mt-1">Em Andamento</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="kanban">
          {loading && (
            <div className="text-center py-8">
              <p className="text-gray-600">A carregar leads...</p>
            </div>
          )}
          
          {!loading && leads.length > 0 && (
            <div className="mb-4 flex items-center gap-4 p-4 bg-gray-100 rounded-lg border-2 border-gray-300">
              <Checkbox 
                id="select-all"
                checked={selectedLeads.length === leads.length && leads.length > 0}
                onCheckedChange={selectAllLeads}
              />
              <Label htmlFor="select-all" className="cursor-pointer font-semibold">
                {selectedLeads.length === leads.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
              </Label>
              {selectedLeads.length > 0 && (
                <Badge className="bg-blue-600 text-white">
                  {selectedLeads.length} lead(s) selecionada(s)
                </Badge>
              )}
            </div>
          )}
          
          {!loading && leads.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-600">Nenhuma lead encontrada nesta categoria.</p>
            </div>
          )}
          
          <div className="overflow-x-auto">
            <div className="flex gap-4 min-w-max">
          {columns.map((column) => (
            <div key={column.id} className="w-80 flex-shrink-0" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, column.id)}>
              <Card className="border-2 border-gray-300">
                <CardHeader className={`${column.color} border-b-2 border-gray-300`}>
                  <CardTitle className="text-lg font-bold flex items-center justify-between">
                    {column.title}
                    <Badge variant="outline" className="bg-white">{leads.filter(l => l.status === column.id).length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3 min-h-[500px] bg-gray-50">
                  {leads.filter(lead => lead.status === column.id).map((lead) => (
                    <Card key={lead.id} className={`hover:shadow-lg transition-shadow border-2 bg-white ${lead.isNew ? 'border-green-500 shadow-green-200' : ''} ${selectedLeads.includes(lead.id) ? 'ring-2 ring-blue-500' : ''}`}>
                      <CardContent className="p-4 space-y-2">
                        {lead.source === 'website' && lead.isNew && (
                          <Badge className="bg-green-500 text-white mb-2">
                            🌐 Nova lead do website!
                          </Badge>
                        )}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2 w-full">
                            <Checkbox 
                              checked={selectedLeads.includes(lead.id)}
                              onCheckedChange={() => toggleLeadSelection(lead.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-1 flex-shrink-0"
                            />
                            <div 
                              className="flex items-start gap-2 flex-1 cursor-grab active:cursor-grabbing hover:bg-gray-50 p-1 rounded transition-colors" 
                              draggable="true"
                              onDragStart={(e) => handleDragStart(e, lead)}
                            >
                              <GripVertical className="h-5 w-5 text-gray-400 hover:text-gray-600 flex-shrink-0 mt-1" />
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-black">{lead.name}</h3>
                                {lead.company && <p className="text-sm text-gray-600 truncate">🏢 {lead.company}</p>}
                                {lead.position && <p className="text-sm text-gray-600 truncate">💼 {lead.position}</p>}
                                {lead.agency && <p className="text-sm text-gray-600 truncate">📍 {lead.agency}</p>}
                              </div>
                              <div className={`w-3 h-3 rounded-full ${temperatureColors[lead.temperature]} flex-shrink-0 mt-1`} title={lead.temperature} />
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 space-y-1 ml-7">
                          <p>📞 {lead.phone}</p>
                          {lead.email && <p>✉️ {lead.email}</p>}
                          <p className="text-xs text-blue-600 font-semibold">👤 {lead.assignedTo}</p>
                        </div>
                        <Button variant="outline" size="sm" className="w-full mt-2 ml-7" onClick={() => setSelectedLead(lead)}>Ver Detalhes</Button>
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            </div>
          ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold heading-luxury">Detalhes da Lead</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nome</Label>
                  <Input value={selectedLead.name} onChange={(e) => setSelectedLead({...selectedLead, name: e.target.value})} className="border-2" />
                </div>
                <div>
                  <Label>Empresa</Label>
                  <Input value={selectedLead.company || ''} onChange={(e) => setSelectedLead({...selectedLead, company: e.target.value})} className="border-2" />
                </div>
                {activeCategory === 'recrutamento' && (
                  <>
                    <div>
                      <Label>Position</Label>
                      <Input value={selectedLead.position || ''} onChange={(e) => setSelectedLead({...selectedLead, position: e.target.value})} className="border-2" />
                    </div>
                    <div>
                      <Label>Agência</Label>
                      <Input value={selectedLead.agency || ''} onChange={(e) => setSelectedLead({...selectedLead, agency: e.target.value})} className="border-2" />
                    </div>
                  </>
                )}
                <div>
                  <Label>Telefone</Label>
                  <Input value={selectedLead.phone} onChange={(e) => setSelectedLead({...selectedLead, phone: e.target.value})} className="border-2" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={selectedLead.email} onChange={(e) => setSelectedLead({...selectedLead, email: e.target.value})} className="border-2" />
                </div>
                <div>
                  <Label>Temperatura</Label>
                  <Select value={selectedLead.temperature} onValueChange={(value) => setSelectedLead({...selectedLead, temperature: value})}>
                    <SelectTrigger className="border-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="frio">🧊 Frio</SelectItem>
                      <SelectItem value="morno">🌤️ Morno</SelectItem>
                      <SelectItem value="quente">🔥 Quente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={selectedLead.status} onValueChange={(value) => setSelectedLead({...selectedLead, status: value})}>
                    <SelectTrigger className="border-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {columns.map(col => (<SelectItem key={col.id} value={col.id}>{col.title}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea value={selectedLead.notes} onChange={(e) => setSelectedLead({...selectedLead, notes: e.target.value})} className="border-2 min-h-[120px]" placeholder="Adicione notas sobre esta lead..." />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSelectedLead(null)}>Cancelar</Button>
                <Button className="bg-gradient-to-r from-gold to-gold-light text-black hover:from-gold-light hover:to-gold" onClick={() => updateLead(selectedLead)}>Guardar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Distribuição de Leads */}
      <Dialog open={showDistributeModal} onOpenChange={setShowDistributeModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold heading-luxury">
              Distribuir Leads Selecionadas
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-1">
                {selectedLeads.length} lead(s) selecionada(s)
              </p>
              <p className="text-xs text-blue-700">
                Selecione o utilizador para atribuir estas leads
              </p>
            </div>
            
            <div>
              <Label>Atribuir a:</Label>
              <Select onValueChange={(value) => distributeLeads(value)}>
                <SelectTrigger className="border-2">
                  <SelectValue placeholder="Selecione um utilizador" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.active).map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDistributeModal(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Distribuição por Quantidade */}
      <Dialog open={showDistributeByQuantityModal} onOpenChange={setShowDistributeByQuantityModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold heading-luxury">
              Distribuir Leads por Quantidade
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 mb-2">
                Categoria: {categories.find(c => c.id === activeCategory)?.name}
              </p>
              <p className="text-xs text-blue-700">
                Distribui automaticamente as primeiras X leads não atribuídas desta categoria
              </p>
            </div>
            
            <div>
              <Label>Quantidade de leads a distribuir:</Label>
              <Input
                type="number"
                min="1"
                value={distributeQuantity}
                onChange={(e) => setDistributeQuantity(e.target.value)}
                className="border-2"
                placeholder="Ex: 50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Insira o número de leads que deseja distribuir automaticamente
              </p>
            </div>
            
            <div>
              <Label>Atribuir a:</Label>
              <Select onValueChange={(value) => distributeLeadsByQuantity(value)}>
                <SelectTrigger className="border-2">
                  <SelectValue placeholder="Selecione um utilizador" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.active).map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowDistributeByQuantityModal(false);
                setDistributeQuantity('');
              }}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Gestão de Utilizadores */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-3xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold heading-luxury">
              Gestão de Utilizadores
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Lista de Utilizadores */}
            <div>
              <h3 className="font-bold text-lg mb-3">Utilizadores Ativos</h3>
              <div className="space-y-2">
                {users.map(user => (
                  <Card key={user.id} className="border-2">
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-gold to-gold-light rounded-full flex items-center justify-center">
                          <span className="text-lg font-bold text-black">
                            {user.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-black">{user.name}</p>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                      </div>
                      {user.id !== 1 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => {
                            if (confirm(`Desativar utilizador ${user.name}?`)) {
                              setUsers(users.map(u => 
                                u.id === user.id ? {...u, active: false} : u
                              ));
                            }
                          }}
                        >
                          Desativar
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Adicionar Novo Utilizador */}
            <div className="border-t-2 pt-6">
              <h3 className="font-bold text-lg mb-3">Adicionar Novo Utilizador</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome *</Label>
                    <Input 
                      value={newUser.name} 
                      onChange={(e) => setNewUser({...newUser, name: e.target.value})} 
                      className="border-2" 
                      placeholder="Nome completo"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      type="email"
                      value={newUser.email} 
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})} 
                      className="border-2" 
                      placeholder="email@exemplo.pt"
                    />
                  </div>
                </div>
                <div>
                  <Label>Password</Label>
                  <Input 
                    type="password"
                    value={newUser.password} 
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})} 
                    className="border-2" 
                    placeholder="Password (opcional para agora)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    * No sistema final, será necessário definir uma password
                  </p>
                </div>
                <Button 
                  onClick={addUser}
                  className="bg-gradient-to-r from-gold to-gold-light text-black hover:from-gold-light hover:to-gold w-full"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Adicionar Utilizador
                </Button>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setShowUserModal(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMDashboard;
